import React from 'react'
import { observer } from 'mobx-react-lite'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import Drawer from '@mui/material/Drawer'
import { AppBarContainerWithDrawerStore } from './AppBarContainerWithDrawerStore'
import useStore from 'reusable/mobx-store-utils'
import Box from '@mui/system/Box'
import Stack from '@mui/system/Stack'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Theme, useTheme } from 'theme/useTheme'
import Announcements from 'features/main/main-router/org-guard/main-container/announcement/Announcements'
import { useThemeStore } from 'theme/ThemeStore'

export interface AppBarContainerDrawerProps { narrow?: boolean }
export interface AppBarContainerWithDrawerProps {
   title?: string | React.ReactElement
   rightButtons?: React.ReactElement
   leftIcon?: React.ReactElement
   drawer?: React.FunctionComponent<AppBarContainerDrawerProps>
   initialDrawerOpen?: boolean
   setStore?: (store: AppBarContainerWithDrawerStore) => any
   hideDrawer?: boolean
   drawerFooter?: React.ReactElement
}

export const AppBarContainerWithDrawer = observer(function AppBarContainerWithDrawer
   (props: React.PropsWithChildren<AppBarContainerWithDrawerProps>) {
   const theme = useTheme()
   const themeStore = useThemeStore()
   const bigScreen = useMediaQuery(theme.breakpoints.up('md'))

   const appBarContainerWithDrawerStore = useStore(source => new AppBarContainerWithDrawerStore(source), {
      bigScreen,
      ...props,
      initialDrawerOpen: props.initialDrawerOpen,
   })

   const iconButtonSX = (theme: Theme) => ({ marginRight: theme.spacing(2) })

   return <Stack sx={{ height: '100vh', backgroundColor: themeStore.backgroundColor }}>
      <Box>
         <Announcements />
         <AppBar
            position="static"
            sx={{ zIndex: theme.zIndex.drawer + 1 }}
         >
            <Toolbar>
               {props.leftIcon && (
                  <IconButton
                     edge="start"
                     sx={iconButtonSX}
                     color="inherit"
                     size="large">
                     {props.leftIcon}
                  </IconButton>
               )}
               <Box style={{ flexGrow: 1 }}>
                  {typeof props.title === 'string' ? (
                     <Typography color="inherit" variant="h6">
                        {props.title}
                     </Typography>
                  ) : (
                     props.title
                  )}
               </Box>
               {props.rightButtons}
            </Toolbar>
         </AppBar>
      </Box>
      <Stack sx={{
         flexGrow: 1,
         overflow: 'hidden',
         flexDirection: 'row',
      }}>
         {!props.hideDrawer && (
            <Drawer
               variant='persistent'
               open
               onClose={appBarContainerWithDrawerStore.onClose}
               sx={{
                  position: 'relative',
                  display: 'flex',
               }}
               PaperProps={{
                  sx: {
                     position: 'static',
                     zIndex: 'auto',
                     background: 'none',
                  },
               }}
            >
               <Box sx={{
                  width: appBarContainerWithDrawerStore.open ? theme.spacing(35) : theme.spacing(7),
                  overflowX: 'hidden',
                  transition: theme.transitions.create('width', {
                     easing: theme.transitions.easing.sharp,
                     duration: theme.transitions.duration.enteringScreen,
                  }),

               }} >
                  {!!props.drawer && <props.drawer narrow={!appBarContainerWithDrawerStore.open} />}
                  <Stack direction={'row'} justifyContent={'space-between'} sx={{ position: 'absolute', bottom: theme.spacing(1), width: '100%' }}>
                     {appBarContainerWithDrawerStore.open && <Box sx={{ pt: 1, ml: 1 }}>{props.drawerFooter}
                     </Box>}
                     <IconButton
                        onClick={appBarContainerWithDrawerStore.drawerToggle}
                        sx={{ mx: 1 }}
                     >
                        {appBarContainerWithDrawerStore.open ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
                     </IconButton>
                  </Stack>
               </Box>
            </Drawer>
         )}
         <Stack sx={{ flexGrow: 1, overflowY: 'auto', '& > *': { flexGrow: 1 } }}>
            {props.children}
         </Stack>
      </Stack>
   </Stack>
}
)
